<template>
	<div :class="{'page-fixed-height': !activePanel}">
		<modal :show.sync="showZoomModal" effect="custom" class="account-selection-modal" :title="activeResource.name" :backdrop="true">
			<div slot="modal-body" class="modal-body center">
				<img class="zoomed-image" :src="activeResource.url">
			</div>
			<div slot="modal-footer" class="modal-footer">
			</div>
		</modal>
		<div class="sideways-page-one" :class="{'disabled': !activePanel}">
			<!-- BEGIN PAGE BAR -->
			<div class="page-bar">
				<breadcrumb :crumbs="breadcrumbArray"></breadcrumb>
			</div>
			<!-- END PAGE BAR -->
			<!-- BEGIN PAGE TITLE-->
			<h1 class="page-title">Resources</h1>
			<!-- END PAGE TITLE-->
			<div class="note note-info">
				Manage your business resources for use across your locations.
			</div>
			<div class="margin-top-20" v-if="folders.id">
				<div class="margin-bottom-25" :class="{'col-sm-4': activePanel, 'col-sm-12': !activePanel}">
					<div class="jstree-default">
						<ul class="jstree-container-ul jstree-children">
							<li class="jstree-node jstree-last" :class="{'jstree-open': folders.expanded, 'jstree-closed': !folders.expanded}">
								<i class="jstree-icon jstree-ocl" @click="expandNode(folders)"></i>
								<a class="jstree-anchor" @click="activePageUpdate(1, folders, true)" :class="{'jstree-clicked': folders.id === activeFolder.id}">
									{{ activeLocationId ? activeLocationName : activeBusinessName }} Resources
									<i class="fa" :class="{'fa-eye': folders.is_shared, 'fa-eye-slash': !folders.is_shared}" v-if="!activeLocationId"></i>
									<i class="fa fa-pencil" @click.stop="manageFolder(folders)"></i>
								</a>
								<ul class="jstree-children" v-if="folders.expanded">
									<li class="jstree-node jstree-leaf" :class="{'jstree-last': !folders.children || !folders.children.length}">
										<i class="jstree-icon jstree-ocl"></i>
										<a class="jstree-anchor" @click="throttledCreate(folders)"><button class="btn blue btn-xs">Create Folder</button></a>
									</li>
									<template v-for="(folder, index) in folders.children">
										<resource-folder :folder="folder" :activeFolder="activeFolder.id" :last="index === folders.children.length - 1" @expandNode="expandNode" @createFolder="createFolder" @manageFolder="manageFolder" @getResources="activePageUpdate"></resource-folder>
									</template>
								</ul>
							</li>
						</ul>
					</div>
				</div>
				<div :class="{'col-sm-8': activePanel, 'col-sm-12': !activePanel}">
					<div v-if="!activeFolder.id && !allFoldersView" class="center margin-top-20">Select a folder to view resources.</div>
					<div v-if="activeFolder.id || allFoldersView">
						<div class="resource-header clearfix">
							<file-upload v-if="!allFoldersView" :folderId="activeFolder.id" @savingUpdate="checkSaving" @success="imageUploaded"></file-upload>
							<button v-if="!isSaving" type="button" class="btn btn-sm btn-outline btn-default margin-bottom-10" @click="toggleFilterPanel()">{{ filterCollapse ? 'Show' : 'Hide' }} Filter and Search Options</button>
						</div>
						<div class="portlet box blue-hoki portlet-blue-override">
							<div class="portlet-body" :class="{'display-hide': filterCollapse}">
								<form role="form" @submit.prevent="advancedResourceSearch()">
									<div class="alert alert-danger" v-if="searchError.length">
										<button class="close" data-close="alert" @click="clearSearchError()"></button>
										<span>{{ searchError }}</span>
									</div>
									<div class="filter-tags" v-if="tags.length">
										<label>Resource Tags <small>(Toggle the tags you want to filter by.)</small></label>
										<div>
											<template v-for="tag in tags">
												<button type="button" class="btn btn-default btn-xs" :class="{'blue': tag.active}" @click="toggleTag(tag)">{{ tag.name }}</button>
											</template>
										</div>
									</div>
									<div class="filter-tags" :class="{'margin-top-20': tags.length}">
										<label>File Types <small>(Toggle the types you want to filter by.)</small></label>
										<div>
											<template v-for="type in fileTypes">
												<button type="button" class="btn btn-default btn-xs" :class="{'blue': type.active}" @click="toggleFileType(type)"><i :class="type.icon"></i> {{ type.name }}</button>
											</template>
										</div>
									</div>
									<div class="form-group form-md-line-input form-md-floating-label margin-top-20">
										<input type="text" class="form-control input-sm" id="search_options_search" :class="{'edited': advancedSearch.search.length}" v-model="advancedSearch.search" :tabindex="activePanel ? 0 : -1">
										<label for="search_options_search">Search</label>
										<span class="help-block persist">Select search range below.</span>
									</div>
									<div class="md-radio-inline margin-top-25">
										<div class="md-radio" v-for="option in advancedSearch.searchOptions">
											<input type="radio" :id="`advanced_search_radio_${option.value}`" :name="`advanced_search_radio_${option.value}`" class="md-radiobtn" v-model="advancedSearch.range" :value="option.value">
											<label :for="`advanced_search_radio_${option.value}`">
												<span></span>
												<span class="check"></span>
												<span class="box"></span>
												{{ option.name }}
											</label>
										</div>
									</div>
									<div class="form-actions right">
										<button type="button" class="btn btn-default" :tabindex="activePanel ? 0 : -1" @click="resetResourceSearch()"> Reset Search </button>
										<button type="submit" class="btn blue" :tabindex="activePanel ? 0 : -1"><i class="fa fa-search"></i> Search </button>
									</div>
								</form>
							</div>
						</div>
						<div class="cbp cbp-caption-active cbp-caption-overlayBottomReveal cbp-ready">
							<div class="cbp-wrapper-outer">
								<div class="cbp-wrapper">
									<loading-screen :show="loadingResourceData || isSaving" :color="'#3598dc'" :display="'inline'"></loading-screen>
									<div v-if="!loadingResourceData && !isSaving">
										<div class="clearfix">
											<dropdown>
												<button slot="button" type="button" class="btn btn-sm btn-outline dropdown-toggle btn-default" data-toggle="dropdown">
													Sort By<span v-if="sortBy.field"><b>: {{ sortOptions[sortBy.field] }} <i class="fa fa-sort-alpha-asc" v-if="sortBy.order === 'asc'"></i><i class="fa fa-sort-alpha-desc" v-if="sortBy.order === 'desc'"></i></b></span>
													<i class="fa fa-angle-down"></i>
												</button>
												<ul slot="dropdown-menu" class="dropdown-menu">
													<li class="rounded-button-group-block">
														<div class="btn-group" data-toggle="buttons">
															<label class="btn blue btn-outline" for="sort_asc" :class="{'active': sortBy.order === 'asc'}">
																<input type="radio" class="toggle" id="sort_asc" value="asc" v-model="sortBy.order">
																<i class="fa fa-sort-alpha-asc"></i>
															</label>
															<label class="btn blue btn-outline" for="sort_desc" :class="{'active': sortBy.order === 'desc'}">
																<input type="radio" class="toggle" id="sort_desc" value="desc" v-model="sortBy.order">
																<i class="fa fa-sort-alpha-desc"></i>
															</label>
														</div>
													</li>
													<li :class="{'active': sortBy.field === 'name'}">
														<a @click="setSortField('name')">Name</a>
													</li>
													<li :class="{'active': sortBy.field === 'size'}">
														<a @click="setSortField('size')">Size</a>
													</li>
													<li :class="{'active': sortBy.field === 'created_at'}">
														<a @click="setSortField('created_at')">Date Added</a>
													</li>
												</ul>
											</dropdown>
											<page-results class="pull-right" :totalResults="totalResults" :activePage="activePage" :allowPageChange="false" :resultsPerPageOverride="27"></page-results>
										</div>
										<div class="margin-top-20">
											<pagination :passedPage="activePage" :numPages="numPages" @activePageChange="activePageUpdate"></pagination>
										</div>
										<div class="cbp-no-files" v-if="!currentResources.length">There are currently no resources in this folder.</div>
										<div class="clearfix">
											<template v-for="resource in currentResources">
												<div class="cbp-item col-sm-4" ref="resource">
													<div class="cbp-item-wrapper">
														<div class="cbp-caption">
															<div class="cbp-caption-defaultWrap" @click="zoom(resource)">
																<img :src="resource.url">
																<div class="image-zoom"><i class="fa fa-search-plus"></i></div>
															</div>
															<div class="cbp-caption-activeWrap">
																<div class="cbp-l-caption-alignCenter">
																	<div class="cbp-l-caption-body">
																		<button class="btn btn-default btn-xs" @click="manageResource(resource)">Manage Resource</button>
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											</template>
										</div>
										<div class="margin-top-20">
											<pagination :passedPage="activePage" :numPages="numPages" @activePageChange="activePageUpdate"></pagination>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- PANEL COMPONENTS START -->
		<div class="sideways-page-two" :class="{'active': !activePanel, 'inactive': activePanel}" :style="{height: computedHeight}">
			<div class="sideways-blank-fold">
				<div class="text-center">
					<button type="button" class="btn blue btn-lg margin-bottom-10" @click="closePanel()">Return to this page</button>
				</div>
			</div>
			<create-folder></create-folder>
			<manage-folder></manage-folder>
			<manage-resource></manage-resource>
		</div>
		<!-- PANEL COMPONENTS END -->

	</div>
</template>
<script>
import $ from 'jquery'
import Breadcrumb from '../modules/Breadcrumb'
import Dropdown from '../modules/Dropdown'
import LoadingScreen from '../modules/LoadingScreen'
import Modal from '../modules/Modal'
import PageResults from '../modules/PageResults'
import Pagination from '../modules/Pagination'
import Resources from '../../controllers/Resources'
import { throttle, debounce } from 'lodash'
import CreateFolder from './Resources/CreateFolder'
import ManageFolder from './Resources/ManageFolder'
import ManageResource from './Resources/ManageResource'
import ResourceFolder from './Resources/ResourceFolder'
import FileUpload from './Resources/FileUpload'
import { mapGetters } from 'vuex'

/**
 * Resources module is a page for the resources dashboard and management processes.
 * @class Resources
 * @since 0.0.9
 */

export default {
	data () {
		return {
			breadcrumbArray: [
				{name: 'Resources', link: false}
			],
			activePanel: true,
			filterCollapse: true,
			computedHeight: 0,
			folders: {},
			currentResources: [],
			activeFolder: {},
			showZoomModal: false,
			activeResource: {},
			numPages: 0,
			totalResults: 0,
			activePage: 1,
			loadingResourceData: false,
			isSaving: false,
			searchError: '',
			tags: [],
			fileTypes: [{name: 'image', icon: 'fa fa-file-image-o', active: false}, {name: 'video', icon: 'fa fa-file-video-o', active: false}, {name: 'documents', icon: 'fa fa-file-text-o', active: false}, {name: 'other', icon: 'fa fa-file-o', active: false}],
			advancedSearch: {
				search: '',
				range: 'this_folder',
				searchOptions: [
					{name: 'All Folders', value: 'all_folders'},
					{name: 'This Folder', value: 'this_folder'}
				]
			},
			sortBy: {
				field: 'name',
				order: 'asc'
			},
			sortOptions: {
				name: 'Name',
				size: 'Size',
				created_at: 'Date Added'
			},
			allFoldersView: false,
			folderRefresh: {},
			folderUpdateRefresh: {}
		}
	},
	/**
	 * Run on `created` to initialize the throttle events and to check the current process step.
	 * @function
	 * @returns {undefined}
	 * @memberof Resources
	 * @version 0.0.9
	 */
	created () {
		this.throttledCreate = throttle(folder => {
			this.createFolder(folder)
		}, 500, { 'trailing': false })

		if (this.$route.name !== 'Resources') {
			this.activePanel = false
		}

		let _this = this

		this.computeHeight()

		$(window).on('resize', debounce(() => {
			_this.computeHeight()
			_this.calculateImageHeight()
		}, 200))

		this.getFolders()
		this.getTags()
	},
	computed: mapGetters([
		'activeBusinessId',
		'activeBusinessName',
		'activeLocationId',
		'activeLocationName'
	]),
	methods: {
		/**
		 * To compute height to display based on window height and navbar
		 * @function
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		computeHeight () {
			const height = $(window).height() - 50
			this.computedHeight = height + 'px'
		},
		/**
		 * To close anything active in the side panel by redirecting to the resources dashboard route
		 * @function
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		closePanel () {
			this.$router.push('/app/resources')
		},
		/**
		 * To toggle the filter panel, initially set to closed
		 * @function
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		toggleFilterPanel () {
			this.filterCollapse = !this.filterCollapse
		},
		/**
		 * To initialize the create folder process by redirecting to the create folder route
		 * @function
		 * @param {object} folder - The parent folder where the user is creating a new folder
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		createFolder (folder) {
			this.folderRefresh = folder
			this.$router.push('/app/resources/create_folder/' + folder.id)
		},
		/**
		 * To initialize the manage folder process by redirecting to the manage folder route
		 * @function
		 * @param {object} folder - The folder being managed
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		manageFolder (folder) {
			this.folderUpdateRefresh = folder
			this.$router.push('/app/resources/manage_folder/' + folder.id)
		},
		/**
		 * To initialize the manage resource process by redirecting to the manage resource route
		 * @function
		 * @param {object} resource - The resource being managed
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		manageResource (resource) {
			this.$router.push('/app/resources/manage_resource/' + resource.id)
		},
		/**
		 * To pull in folders under the selected parent id (or root)
		 * @function
		 * @param {integer} parentId - The id of the parent folder being pulled from.
		 * @param {object} folder - The parent folder.
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		getFolders (parentId, folder) {
			let _this = this
			const businessId = this.activeBusinessId
			const pageNumber = 1
			const recordsPerPage = 100
			if (!parentId) {
				parentId = 0
			}
			const locationId = this.activeLocationId

			return Resources.getFolders(businessId, locationId, parentId, pageNumber, recordsPerPage).then(response => {
				if (parentId === 0) {
					_this.folders = Object.assign({}, _this.folders, response.payload.folders[0])
					_this.$set(_this.folders, 'expanded', false)
					_this.$set(_this.folders, 'children_fetched', false)
				} else {
					_this.$set(folder, 'children_fetched', true)
					_this.$set(folder, 'children', response.payload.folders)
				}
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'folder_not_found':
						_this.$set(folder, 'children_fetched', true)
						_this.$set(folder, 'children', [])
						break

					case 'business_not_found':
						break

					case 'invalid_page_number':
						break

					default:
						// No default
					}
				}
			}))
		},
		/**
		 * To pull in all tags belonging to the business or location
		 * @function
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		getTags () {
			let _this = this
			const businessId = this.activeBusinessId
			const locationId = this.activeLocationId
			const pageNumber = 1
			const recordsPerPage = 100

			return Resources.getTags(businessId, locationId, pageNumber, recordsPerPage).then(response => {
				_this.tags = response.payload.tags.map(tag => {
					tag.active = false
					return tag
				})
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'tags_not_found':
						_this.tags = []
						break
					default:
						// No default
					}
				}
			}))
		},
		/**
		 * To expand or collapse the selected folder and to pull in all child folders.
		 * @function
		 * @param {object} folder - The folder being toggled.
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		expandNode (folder) {
			if (folder.expanded) {
				this.$set(folder, 'expanded', false)
			} else {
				this.$set(folder, 'expanded', true)

				this.getFolders(folder.id, folder)
			}
		},
		/**
		 * To format the search criteria for use in the api call
		 * @function
		 * @returns {object} - An object of the formated search data
		 * @memberof Resources
		 * @version 0.0.9
		 */
		formatSearchCriteria () {
			let searchCriteria = {}

			if (this.advancedSearch.search.length > 2) {
				searchCriteria.search_query = this.advancedSearch.search
				searchCriteria.search_in = 'name'
			}

			searchCriteria.tags = []
			this.tags.forEach(tag => {
				if (tag.active) {
					searchCriteria.tags.push(tag.id)
				}
			})

			return searchCriteria
		},
		/**
		 * To preload images to avoid delayed loading in the UI.
		 * @function
		 * @returns {object} A promise that will return either a success object or an error object.
		 * @memberof Resources
		 * @version 0.0.9
		 */
		preloadImages () {
			let _this = this

			return new Promise((resolve, reject) => {
				if (!_this.currentResources.length) {
					resolve()
				}

				_this.currentResources.forEach(resource => {
					/* eslint-disable no-undef */
					let img = new Image()
					img.src = resource.url
				})

				resolve()
			})
		},
		/**
		 * To fetch the resources in the selected folder.
		 * @function
		 * @param {object} folder - The folder being fetched.
		 * @param {integer} pageNumber - The current page that we are retrieving results for.
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		getResources (folder, pageNumber) {
			this.loadingResourceData = true
			this.currentResources = []

			let _this = this
			const businessId = this.activeBusinessId
			const recordsPerPage = 27
			const searchCriteria = this.formatSearchCriteria()
			const sortCriteria = {sort_by: this.sortBy.field, sort_order: this.sortBy.order}
			const locationId = this.activeLocationId

			if (!pageNumber) {
				pageNumber = this.activePage
			}

			let folderId = folder.id
			if (this.advancedSearch.range === 'all_folders') {
				folderId = null
				this.allFoldersView = true
				this.activeFolder = Object.assign({})
			} else {
				this.allFoldersView = false
				this.activeFolder = Object.assign({}, folder)
			}

			console.log('getResources', businessId, locationId, folderId, pageNumber, recordsPerPage, searchCriteria, sortCriteria)
			return Resources.getResources(businessId, locationId, folderId, pageNumber, recordsPerPage, searchCriteria, sortCriteria).then(response => {
				_this.currentResources = response.payload.files
				_this.numPages = response.payload.number_of_pages
				_this.totalResults = response.payload.number_of_records

				_this.preloadImages().then(response => {
					setTimeout(() => {
						_this.loadingResourceData = false
						setTimeout(() => {
							_this.calculateImageHeight()
						}, 0)
					}, 500)
				}).catch(error => {
					console.log(error)
				})
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'file_not_found':
						_this.numPages = 0
						_this.totalResults = 0
						break

					case 'business_not_found':
						_this.numPages = 0
						_this.totalResults = 0
						_this.$swal({
							title: 'Error',
							html: 'There is an issue with your business account. Please contact your admin.',
							type: 'warning'
						})
						break

					case 'invalid_page_number':
						_this.numPages = e.responseJSON.payload.number_of_pages
						_this.totalResults = e.responseJSON.payload.number_of_records
						if (_this.numPages !== 0) {
							_this.getResources(this.activeFolder, e.responseJSON.payload.number_of_pages)
						}
						break

					default:
						// No default
					}
				}
				_this.loadingResourceData = false
			}))
		},
		/**
		 * To set the height of each resource node based on the current width.
		 * @function
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		calculateImageHeight () {
			if (!this.$refs.resource || !this.$refs.resource.length) {
				return
			}

			let height = window.getComputedStyle(this.$refs.resource[0]).width

			this.$refs.resource.forEach(ref => {
				ref.style.height = height
			})
		},
		/**
		 * To open the zoom modal to display the selected resource.
		 * @function
		 * @param {object} resource - The resource being zoomed on.
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		zoom (resource) {
			this.showZoomModal = true
			this.activeResource = Object.assign({}, resource)
		},
		/**
		 * To check whether or not a file is currently being uploaded.
		 * @function
		 * @param {boolean} val - Whether or not a file is currently being uploaded.
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		checkSaving (val) {
			this.isSaving = val
		},
		/**
		 * Called when a file is uploaded to give the user the opportunity to manage the file.
		 * @function
		 * @param {integer} val - The id of the uploaded file.
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		imageUploaded (val) {
			this.manageResource({id: val})
			this.getResources(this.activeFolder, this.activePage)
		},
		/**
		 * To toggle a tag on and off as a filter option.
		 * @function
		 * @param {object} tag - The tag being toggled
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		toggleTag (tag) {
			tag.active = !tag.active
		},
		/**
		 * To toggle a file type on and off as a filter option.
		 * @function
		 * @param {object} type - The type being toggled
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		toggleFileType (type) {
			type.active = !type.active
		},
		/**
		 * To update the currently active pagination page.
		 * @function
		 * @param {integer} val - An integer representing the page number that we are updating to.
		 * @param {object} folder - If the user is also selecting a new folder then it will be passed here.
		 * @param {boolean} override - Whether or not to override the if condition.
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		activePageUpdate (val, folder, override) {
			if (parseInt(this.activePage) !== parseInt(val)) {
				this.activePage = val
				if (folder) {
					this.getResources(folder, this.activePage)
				} else {
					this.getResources(this.activeFolder, this.activePage)
				}
			} else if (override) {
				if (folder) {
					this.getResources(folder, this.activePage)
				} else {
					this.getResources(this.activeFolder, this.activePage)
				}
			}
		},
		/**
		 * To update the sort by field and make a backend call to pull the sorted data.
		 * @function
		 * @param {string} field - The field that the user wants to sort by.
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		setSortField (field) {
			if (field !== this.sortBy.field) {
				this.sortBy.field = field
				if (parseInt(this.activePage) === 1) {
					this.getResources(this.activeFolder, 1)
				} else {
					this.activePageUpdate(1, this.activeFolder)
				}
			}
		},
		/**
		 * To clear the current resource search criteria.
		 * @function
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		resetResourceSearch () {
			this.advancedSearch = {
				search: '',
				range: 'this_folder',
				searchOptions: [
					{name: 'All Folders', value: 'all_folders'},
					{name: 'This Folder', value: 'this_folder'}
				]
			}

			this.tags.forEach(tag => {
				tag.active = false
			})
			this.fileTypes.forEach(type => {
				type.active = false
			})

			this.clearSearchError()
		},
		/**
		 * To send the advanced search options to the backend to fetch resources.
		 * @function
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		advancedResourceSearch () {
			this.clearSearchError()
			if (this.advancedSearch.search.length && this.advancedSearch.search.length < 3) {
				this.searchError = 'In order to use the search field your term must be at least 3 characters.'
			} else {
				if (parseInt(this.activePage) === 1) {
					this.getResources(this.activeFolder, 1)
				} else {
					this.activePageUpdate(1, this.activeFolder)
				}
			}
		},
		/**
		 * To clear the current search error.
		 * @function
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		clearSearchError () {
			this.searchError = ''
		},
		/**
		 * To recursively search through the folder tree to find a folder
		 * @function
		 * @param {object} folder - The folder being searched in the current iteration
		 * @param {integer} folderId - The folder id being search for
		 * @returns {object} The folder that the user is searching for or null
		 * @memberof Resources
		 * @version 0.0.9
		 */
		findFolderInTree (folder, folderId) {
			if (folder.id === folderId) {
				return folder
			} else if (folder.children && folder.children.length) {
				let result = null
				for (let i = 0, x = folder.children.length; result === null && i < x; i++) {
					result = this.findFolderInTree(folder.children[i], folderId)
				}

				return result
			}
			return null
		}
	},
	components: {
		Breadcrumb,
		Dropdown,
		LoadingScreen,
		Modal,
		PageResults,
		Pagination,
		CreateFolder,
		ManageFolder,
		ManageResource,
		ResourceFolder,
		FileUpload
	},
	watch: {
		/**
		 * Watch `$route` so that we can check if we are on the proper panel after each change.
		 * @function
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		'$route' () {
			if (this.$route.name !== 'Resources') {
				this.activePanel = false
			} else {
				this.activePanel = true
				if (!this.folders.id) {
					this.getFolders()
				} else {
					if (this.folderRefresh.id) {
						this.getFolders(this.folderRefresh.id, this.folderRefresh)
						this.folderRefresh = Object.assign({})
					} else if (this.folderUpdateRefresh.id) {
						const localFolder = this.findFolderInTree(this.folders, this.folderUpdateRefresh.parent_id)
						this.getFolders(this.folderUpdateRefresh.parent_id, localFolder)
						this.folderUpdateRefresh = Object.assign({})
					}
				}
			}
		},
		/**
		 * Watch `sortBy.order` so that we can update the active page and retrieve the resources with the updated sort order.
		 * @function
		 * @returns {undefined}
		 * @memberof Resources
		 * @version 0.0.9
		 */
		'sortBy.order' () {
			if (this.sortBy.field) {
				if (parseInt(this.activePage) === 1) {
					this.getResources(this.activeFolder, 1)
				} else {
					this.activePageUpdate(1, this.activeFolder)
				}
			}
		}
	}
}
</script>
<style>
.cbp.cbp-caption-active.cbp-caption-overlayBottomReveal.cbp-ready {
	height: auto;
}
.cbp.cbp-caption-active.cbp-caption-overlayBottomReveal.cbp-ready .cbp-wrapper {
	position: relative;
	padding: 0 10px 10px;
	min-height: 250px;
}
.cbp-no-files {
	text-align: center;
	margin-top: 10px;
}
.cbp.cbp-caption-active.cbp-caption-overlayBottomReveal.cbp-ready .cbp-item {
	position: relative;
	padding: 10px;
	cursor: pointer;
}
.cbp-item-wrapper {
	padding: 2px;
    border: 1px solid #e7ecf1;
}
.cbp-caption {
	height: 100%;
}
.cbp.cbp-caption-active.cbp-caption-overlayBottomReveal.cbp-ready .cbp-caption-defaultWrap {
	height: 100%;
	display: flex;
    justify-content: center;
    align-items: center;
    background-color: #fff;
}
.cbp-caption-defaultWrap img {
	height: 100%;
}
.cbp-caption-defaultWrap .image-zoom {
	display: none;
 	position: absolute;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.3);
    width: 100%;
    height: calc(100% - 60px);
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 30px;
    color: #fff;
    opacity: 0;
    transition: opacity 0.5s ease;
}
.cbp-caption:hover .cbp-caption-defaultWrap .image-zoom {
	opacity: 1;
}
.cbp-caption-activeWrap {
	background-color: #fff;
}
.btn-wide {
	width: 100%;
}
.cbp-item-wrapper.bordered-button-options {
	border: 2px solid ;
	padding: 10px;
}
.disabled .col-sm-3 {
	width: 50%;
}
.jstree-default .jstree-container-ul .jstree-clicked {
	font-weight: bold;
    background: none;
    box-shadow: none;
}
.zoomed-image {
	max-height: 100%;
	max-width: 100%;
}
.resource-header {
	border-bottom: 1px solid #e7ecf1;
}
.resource-header > .btn {
	float: right;
}
.jstree-anchor i {
	opacity: 0.5;
	font-size: 85%;
}
.jstree-anchor i.fa-eye {
	opacity: 1;
}
.portlet.box.blue-hoki.portlet-blue-override {
	border-color: #e7ecf1;
	border-top-left-radius: 0;
    border-top-right-radius: 0;
    box-shadow: none;
}
.filter-tags label {
	color: #999;
}
.filter-tags button {
	margin: 3px;
}
</style>



// WEBPACK FOOTER //
// Resources.vue?3174a6cf